import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable consistent-return */
import styled, { css } from 'styled-components';
import Link, { BlockLink } from 'web-app/react/components/link/link';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { s2 } from 'web-app/styleguide/spacing';
import { Text } from '@famly/mf_data-display_text';
import { ProfileImageTooltipComposer } from './profile-image-tooltip-composer';
const StyledOverlay = styled.div `
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-radius: 50%;
    background-color: ${props => props.theme.blackScrim};
    height: 100%;
`;
const StyledContainer = styled.div `
    height: 100%;
    width: 100%;
    border-radius: 100%;
    position: relative;

    display: flex;
`;
export const StyledImageContainer = styled('div') `
    ${props => props.size
    ? ''
    : css `
                  height: 100%;
                  width: 100%;
              `}

    width: ${props => (props.size ? `${props.size}px` : 'initial')};
    border-radius: 50%;
    overflow: hidden;
    box-sizing: border-box;
    display: inline-block;
    ${props => {
    if (props.bordered) {
        const width = props.overlap ? 2 : 1;
        const color = props.overlap ? props.theme.invertedText : props.theme.delimiter;
        const border = css `
                border: ${width}px solid ${color};
            `;
        return props.size
            ? css `
                      position: relative;
                      :after {
                          content: '';
                          border-radius: 50%;
                          position: absolute;
                          top: 0;
                          left: 0;
                          right: 0;
                          bottom: 0;
                          ${border}
                      }
                  `
            : border;
    }
}}

    ${props => {
    if (!props.shadow) {
        return '';
    }
    const width = props.overlap ? 2 : 1;
    const color = props.overlap ? props.theme.invertedText : props.theme.delimiter;
    return css `
            box-shadow:
                0 0 ${width}px 0px ${color} inset,
                0 0 ${width}px 0px ${color};
        `;
}};

    ${props => props.overlap
    ? css `
                  ${hasValue(props.size) && hasValue(props.count)
        ? css `
                                /**
                             * Stacking the images using negative margin is preferable as applying 'transform: translateX(...)'
                             * doesn't affect the container of the laid out images, which leaves blank space after the images.
                             */
                                margin-left: -${props.count ? props.size / 2 - props.theme.spacing.spacingMap[s2.spacingType] : 0}px;
                            `
        : css `
                                transform: translateX(${props.count ? props.count * -50 : 0}%);
                            `}
              `
    : css `
                  :not(:first-child) {
                      margin-left: 8px;
                  }
              `}

    ${props => props.onClick
    ? css `
                  cursor: pointer;
              `
    : ''};

    @media not print {
        /* Fixes layout bug in mobile safari causing the border-radius not to be enforced. See: https://gist.github.com/ayamflow/b602ab436ac9f05660d9c15190f4fd7b */
        -webkit-mask-image: -webkit-radial-gradient(white, black);
    }
`;
export const StyledImg = styled('img') `
    max-height: 100%;
    min-width: 100%;
    height: ${props => (props.size ? `${props.size}px` : '100%')};
    width: ${props => (props.size ? `${props.size}px` : 'auto')};
    ${props => props.mute &&
    css `
            opacity: 0.3;
            filter: grayscale(100%);
        `}
`;
export const StyledContainerLink = styled(BlockLink) `
    & + & {
        margin-left: 8px;
    }
`;
const emtpyTypeImageSrc = (placeholderType) => {
    switch (placeholderType) {
        case 'institution':
            return 'img/empty-states/institution.svg';
        case 'room':
            return 'img/empty-states/room.svg';
        case 'default':
        default:
            return 'img/empty-states/profile-image.svg';
    }
};
/**
 * @deprecated use the new MF avatar component `import {Avatar} from '@famly/mf_data-display_avatar'` instead
 */
const ProfileImage = (props) => {
    const { tooltipPlacement, src, mute, bordered, shadow, className, limitCount, size, overlap = true, allowContainerAsLink = true, addLinksToShownImages, tooltipAll, placeholderSrc, placeholderType = 'default', showTooltipOnOverflowingItems = true, } = props;
    const emptyImageSrc = placeholderSrc || emtpyTypeImageSrc(placeholderType);
    if (!Array.isArray(src)) {
        return (_jsx(StyledImageContainer, { className: className, shadow: shadow, bordered: bordered, overlap: overlap, size: size, children: _jsx(StyledImg, { src: src || emptyImageSrc, mute: mute, size: size }) }));
    }
    else if (src.length === 1) {
        const content = (_jsx(ProfileImageTooltipComposer, { items: src, tooltipPlacement: tooltipPlacement, emptyImageSrc: emptyImageSrc, children: _jsx(StyledImageContainer, { className: className, bordered: bordered, overlap: overlap, size: size, onClick: 'onClick' in src[0] ? src[0].onClick : undefined, children: _jsx(StyledImg, { src: src[0].image || emptyImageSrc, mute: mute, size: size }) }) }));
        const first = src[0];
        if ('link' in first && first.link && allowContainerAsLink) {
            return _jsx(Link, { href: first.link, children: content });
        }
        return content;
    }
    const limit = limitCount !== undefined ? limitCount : 1;
    const lastShown = src[limit - 1];
    const sliceEnd = limit < src.length ? limit - 1 : limit;
    return (_jsxs(StyledContainer, { className: className, children: [src.slice(0, sliceEnd).map((source, i) => {
                let content = (_jsx(StyledImageContainer, { bordered: bordered, overlap: overlap, size: size, count: i, onClick: addLinksToShownImages && 'onClick' in source ? source.onClick : undefined, children: _jsx(StyledImg, { src: source.image ? source.image : emptyImageSrc, mute: mute, size: size, onClick: 'onClick' in source ? source.onClick : undefined }) }, i));
                if (tooltipAll) {
                    content = (_jsx(ProfileImageTooltipComposer, { items: source, tooltipPlacement: tooltipPlacement, emptyImageSrc: emptyImageSrc, children: content }, i));
                }
                if ('link' in source && source.link && addLinksToShownImages) {
                    return (_jsx(StyledContainerLink, { href: source.link, children: content }, i));
                }
                return content;
            }), limit < src.length && (_jsx(ProfileImageTooltipComposer, { items: src.slice(limit - 1), tooltipPlacement: tooltipPlacement, emptyImageSrc: emptyImageSrc, disabled: !showTooltipOnOverflowingItems, children: _jsx(StyledImageContainer, { bordered: bordered, count: limit - 1, overlap: overlap, size: size, children: _jsxs(StyledOverlay, { children: [_jsx(StyledImg, { src: lastShown.image ? lastShown.image : emptyImageSrc, mute: true, size: size }), _jsx(Text, { variant: "body", component: "p", position: "absolute", color: "n0", children: `+${src.length - limit + 1}` })] }) }) }))] }));
};
export default ProfileImage;
